import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { Box, CircularProgress, Typography } from "@mui/material";
import { yupResolver } from "@hookform/resolvers/yup";
import isEmpty from "lodash/isEmpty";

//importing custom modules
import { Button } from "../../components";
import { newUserSchema } from "../../yup";
import { getErrorMessage } from "../../utils/common";

// importing styles
import style from "./courier.module.css";
import { colors } from "../../theme/colors";

// importing api
import {
  useUpdateUserMutation,
  useGetUserDetailsQuery,
} from "../../services/api";
import { LabelBox } from "../../components";
import { SingleSelectInput } from "../../components/SelectInput";
import { Prefix } from "../../utils/constants";
import { Input } from "../../components/UserInput";

/**
 * Renders the edit-courier form and submits updates for an existing courier user.
 *
 * Notes:
 * - Requires a valid courier id in the route params.
 * - Email is read-only and not submitted as part of updates.
 * - TODO: Confirm optional fields and update constraints for courier edits.
 *
 * Returns:
 *   The edit-courier page component.
 */
const EditCourier = () => {
  const { id } = useParams();
  const navigation = useNavigate();
  const [updateUser] = useUpdateUserMutation();

  // Get Courier Details mutation hook
  const {
    data: courierDetails,
    isLoading,
    isFetching,
  } = useGetUserDetailsQuery(id ?? "", { skip: !id });

  const [loading, setLoading] = useState<boolean>(false);
  const [message, setMessage] = useState<LabelMessageType | null>(null);

  useEffect(() => {
    if (!id) {
      navigation(-1);
    }
  }, [id, navigation]);

  type CourierFormValues = typeof newUserSchema.__outputType;

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm<CourierFormValues>({
    resolver: yupResolver(newUserSchema),
    mode: "onChange",
  });

  // Hydrate form defaults once the courier record loads.
  useEffect(() => {
    // Avoid overwriting local edits before initial data arrives.
    if (!isEmpty(courierDetails)) {
      reset({
        prefix: (courierDetails)?.prefix ?? "",
        given_name: (courierDetails)?.given_name ?? "",
        surname: (courierDetails)?.surname ?? "",
        mail: (courierDetails)?.mail ?? "",
        phone: (courierDetails)?.phone ?? "",
      });
    }
  }, [courierDetails, reset]);

  // Protect update calls from invalid route ids to avoid NaN payloads.
  /**
   * Submits updated courier fields to the API.
   *
   * Args:
   *   values: Form values validated by the courier schema.
   *
   * Returns:
   *   A promise that resolves when the update completes.
   */
  const onSubmit = async (values: CourierFormValues) => {
    const parsedId = Number(id);
    // Block invalid ids to prevent submitting NaN payloads to the API.
    if (!id || Number.isNaN(parsedId)) {
      setMessage({
        type: "error",
        text: "Invalid courier id. Please refresh and try again.",
      });
      return;
    }
    try {
      setLoading(true);
      let payload: EditUserPayload = {
        id: parsedId,
        given_name: values?.given_name,
        surname: values?.surname,
      };

      // Preserve optional fields on the server when the user leaves them blank.
      // Only send optional fields when present to avoid overwriting with blanks.
      if (values.prefix && values.prefix !== "") {
        payload.prefix = values.prefix;
      }

      if (values.phone && values.phone !== "") {
        payload.phone = values.phone;
      }

      await updateUser({ ...payload }).unwrap();
      setLoading(false);
      setMessage({
        type: "success",
        text: "Courier updated successfully.",
      });
      setTimeout(() => {
        navigation(-1);
      }, 1500);
    } catch (error: unknown) {
      setMessage({
        type: "error",
        text: getErrorMessage(error, "Please enter valid details"),
      });
    } finally {
      setLoading(false);
    }
  };

  if (!id) {
    return null;
  }

  return (
    <Box className={style.container}>
      <Box component="form" onSubmit={handleSubmit(onSubmit)}>
        {(isFetching || isLoading) && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "60vh",
              width: "calc(100vw - 400px)",
              zIndex: 999,
              backgroundColor: "transparent",
              position: "absolute",
            }}
          >
            <CircularProgress size={40} />
          </Box>
        )}
        <Box>
          <Typography className={style.detailHeading}>Courier Details</Typography>
          <Box className={style.flexTop} sx={{ marginTop: "20px" }}>
          <Box className={style.row}>
            <SingleSelectInput
              placeholder="Select Prefix"
              label="Prefix"
              register={register}
              name="prefix"
              errors={errors}
              data={Prefix}
              value={watch("prefix") ?? ""}
            />
          </Box>

          <Box className={style.row}>
            <Input
              placeholder="First Name"
              label="First Name"
              required
              register={register}
              name="given_name"
              errors={errors}
            />
            <Input
              placeholder="Last Name"
              label="Last Name"
              required
              register={register}
              name="surname"
              errors={errors}
            />
          </Box>

          <Box className={style.row}>
            <Input
              placeholder="Email Address"
              label="Email Address"
              required
              register={register}
              name="mail"
              errors={errors}
              disabled={true}
            />
            <Input
              placeholder="Phone Number"
              label="Phone Number"
              register={register}
              name="phone"
              errors={errors}
            />
          </Box>
            {message && (
              <Box mt={2}>
                <LabelBox
                  boxProps={{
                    height: 50,
                    display: "flex",
                    alignItems: "center",
                  }}
                  data={message}
                  onClose={() => setMessage(null)}
                />
              </Box>
            )}
            <Box className={style.buttons}>
              <Button
                sx={{
                  width: "200px",
                  height: "55px",
                }}
                variant="outlined"
                onClick={() => navigation(-1)}
                label="Cancel"
              />
              <Button
                loading={loading}
                sx={{
                  width: "200px",
                  height: "55px",
                  ":hover": { backgroundColor: colors.primary },
                }}
                variant="contained"
                type="submit"
                label="Save"
              />
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default EditCourier;
